'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useChildProfiles } from '@/hooks/use-profile';
import { useTRPC } from '@/lib/trpc/provider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, ClipboardCheck, CheckCircle } from 'lucide-react';
import {
  ClassPicker,
  NONE_OF_THESE_TIMES,
  type PickableClass,
} from '@/components/enrollment/class-picker';
import { AnotherTimeRequest } from '@/components/enrollment/another-time-request';
import { TimezoneMismatchBanner } from '@/components/scheduling/timezone-mismatch-banner';
import Link from 'next/link';

type MidTermPreferencesFormProps = {
  courseId: string;
  courseTitle: string;
  slug: string;
  /** Short-course CTA: target this dated section (request term derives from it). */
  sectionId?: string;
  shortCourse?: {
    startDate: string | null;
    endDate: string | null;
    remainingSessions: number;
    pricePerSessionCents: number;
  } | null;
  /**
   * Set by the CTA on the `waitlist` (course full) state ONLY: the term whose
   * classes this form shows. Its presence pins the class list to that term and
   * offers "None of these times work for us". Absent on `ending` (no carry-over),
   * on "no class running", and on the short-course branch.
   */
  anotherTimeTermId?: string;
};

/**
 * The mid-term enrolment request form.
 *
 * ⚠️ THIS USED TO ASK FOR A WEEKLY AVAILABILITY GRID, AND THAT WAS THE DEFECT.
 *
 * The grid is the open-enrolment picker: families say when they are free, and a
 * matching run builds classes around the answers. Mid-term there is no matching
 * run left to feed. The answer was stored on the request, rendered back to the
 * parent and to the admin as badges, and read by NO scheduling code at all —
 * while the grid greyed out every hour the course's teachers had not marked
 * available for a term that was already matched, so most of it looked dead.
 *
 * On 9 Sep 2026, 31 of 48 catalog courses had no class running at all, and
 * every one of them asked the parent to fill that grid in.
 *
 * So the question now fits the situation:
 *   classes exist  → which one would suit you? (a real choice, on real times)
 *                    — or none of these → when can you attend?
 *   none exist     → nothing about times at all; register interest.
 */
export function MidTermPreferencesForm({
  courseId,
  courseTitle,
  slug,
  sectionId,
  shortCourse,
  anotherTimeTermId,
}: MidTermPreferencesFormProps) {
  const t = useTranslations('public.enrollment.form');
  const tCta = useTranslations('public.enrollment.cta');
  const tc = useTranslations('common');
  const tCat = useTranslations('public.catalog');
  const [selectedChild, setSelectedChild] = useState('');
  const [selectedSectionId, setSelectedSectionId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Which request went in, so the confirmation says the right thing.
  const [submittedKind, setSubmittedKind] = useState<'request' | 'anotherTime'>('request');
  const [anotherTimePending, setAnotherTimePending] = useState(false);

  const { children, isLoading: childrenLoading } = useChildProfiles();
  const trpc = useTRPC();

  // The classes this course is actually running. Scope-gated server-side —
  // the procedure filters on the DISPLAY set and excludes hidden courses.
  //
  // ⚠️ Suppressed on the short-course branch: the CTA has already chosen the
  // dated run and passes it as `sectionId`, so offering a picker there would
  // give two candidate sections and no rule for which wins.
  const { data: classData } = useQuery({
    // Pinned to the CTA's term when "none of these" is on offer, so the classes
    // ruled out are the ones the another-time request is filed against.
    ...trpc.enrollment.getClassesForCourse.queryOptions({
      courseId,
      ...(anotherTimeTermId ? { termId: anotherTimeTermId } : {}),
    }),
    enabled: !sectionId,
  });
  const classes = (classData?.classes ?? []) as PickableClass[];
  const hasClasses = classes.some((c) => !c.hasUnassignedTeacher);

  // Placement gate — per selected child, so a passed/in-band child isn't blocked.
  const { data: eligibility } = useQuery({
    ...trpc.placement.getEnrollmentEligibility.queryOptions({
      childProfileId: selectedChild,
      courseId,
    }),
    enabled: !!selectedChild,
  });
  const block = selectedChild && eligibility && !eligibility.ok ? eligibility : null;
  const needsPlacement =
    !!block &&
    (block.reason === 'placement_required' ||
      block.reason === 'placement_not_passed');

  const submitMutation = useMutation(
    trpc.enrollment.submitEnrollmentRequest.mutationOptions({
      onSuccess: () => {
        setSubmitted(true);
      },
      onError: (error) => {
        toast.error(error.message, { duration: 8000 });
      },
    })
  );

  // The section this request targets: the CTA's choice on the short-course
  // branch, otherwise the parent's pick. "None of these times" is not a class —
  // the sentinel is UI-only and never becomes a `sectionId`.
  const wantsAnotherTime = selectedSectionId === NONE_OF_THESE_TIMES;
  const effectiveSectionId = sectionId ?? (wantsAnotherTime ? '' : selectedSectionId);

  const handleSubmit = () => {
    if (!selectedChild) {
      toast.error(t('selectChildToast'));
      return;
    }
    // ⚠️ NO `selectedSlots.size === 0` REFUSAL HERE ANY MORE. It lived outside
    // the grid block, so removing only the grid would have left the form
    // rendering correctly and permanently unsubmittable.
    if (hasClasses && !effectiveSectionId) {
      toast.error(t('selectClassToast'));
      return;
    }
    if (block) {
      toast.error(block.message, { duration: 6000 });
      return;
    }

    submitMutation.mutate({
      courseId,
      childProfileId: selectedChild,
      // Empty by design: mid-term there is no matching run for availability to
      // feed. The schema no longer demands a non-empty array.
      availabilitySlots: [],
      ...(effectiveSectionId ? { sectionId: effectiveSectionId } : {}),
    });
  };

  if (submitted) {
    const anotherTime = submittedKind === 'anotherTime';
    return (
      <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-center space-y-2">
        <CheckCircle className="h-8 w-8 text-green-600 mx-auto" />
        <p className="text-green-800 font-medium">
          {anotherTime ? t('anotherTimeSubmittedTitle') : t('requestSubmitted')}
        </p>
        <p className="text-green-700 text-sm">
          {/* `|| sectionId`: the short-course branch names its run but loads no
            * class list, and used to thank the family for "registering interest". */}
          {anotherTime
            ? t('anotherTimeSubmittedBody')
            : hasClasses || sectionId
              ? t('requestSubmittedBody')
              : t('interestSubmittedBody')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="text-sm font-medium text-gray-700">
        {t.rich('requestEnrollmentIn', {
          title: courseTitle,
          b: (chunks) => <span className="font-semibold">{chunks}</span>,
        })}
      </div>

      {shortCourse && (
        <div className="rounded-md bg-blue-50 border border-blue-200 p-3 space-y-1 text-sm">
          <p className="font-semibold text-[#2fa5fb]">{t('shortCourse')}</p>
          <p className="text-muted-foreground">
            {shortCourse.startDate ?? t('termStart')}
            {shortCourse.endDate ? ` → ${shortCourse.endDate}` : ` ${t('onward')}`} ·{' '}
            {tCat('sessionCount', { count: shortCourse.remainingSessions })}
          </p>
          {shortCourse.pricePerSessionCents > 0 && (
            <p className="text-muted-foreground">
              {t('pricedPerSession')}
            </p>
          )}
        </div>
      )}

      <Select value={selectedChild} onValueChange={setSelectedChild}>
        <SelectTrigger className="w-full min-h-[44px]" aria-label={tCta('selectChildAria')}>
          <SelectValue placeholder={childrenLoading ? tc('loading') : tCta('selectAChild')} />
        </SelectTrigger>
        <SelectContent>
          {children.map((child) => (
            <SelectItem key={child.id} value={child.id}>
              {child.first_name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {needsPlacement && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          <div className="flex items-center gap-2 text-amber-800 font-medium mb-1">
            <ClipboardCheck className="h-4 w-4" />
            {tCta('placementRequired')}
          </div>
          <p className="text-amber-700 text-xs">
            {block?.message ?? tCta('placementFallback')}
          </p>
          <Link
            href={`/parent/placement/${courseId}`}
            className="mt-2 inline-block text-xs text-[#fc800a] font-medium hover:underline"
          >
            {tCta('takePlacement')}
          </Link>
        </div>
      )}

      {block && !needsPlacement && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
          {block.message}
        </div>
      )}

      {/* Classes exist → choose one. `mode="queue"` because this form is only
        * reached when the course is full or ending: choosing here means "put my
        * child on this class's list", so a full class MUST stay selectable. */}
      {/* Above the class list: the times below are in the profile's zone, and
        * so are the ones a "none of these" family ticks. */}
      {!sectionId && hasClasses && anotherTimeTermId && <TimezoneMismatchBanner />}
      {!sectionId && hasClasses && (
        <ClassPicker
          classes={classes}
          value={selectedSectionId}
          onChange={setSelectedSectionId}
          mode="queue"
          allowNone={!!anotherTimeTermId}
          disabled={submitMutation.isPending || anotherTimePending}
        />
      )}

      {/* No class at all → say so, and ask nothing about times. */}
      {!sectionId && !hasClasses && classData && (
        <div
          className="rounded-lg border border-gray-200 bg-gray-50 p-3"
          data-testid="register-interest-note"
        >
          <p className="text-sm font-semibold text-gray-700">{t('noClassesTitle')}</p>
          <p className="text-xs text-gray-600 mt-0.5">{t('noClassesBody')}</p>
        </div>
      )}

      {/* "None of these times" → ask when, in place of "Request Enrollment".
        * Only while the classes being ruled out are on screen: a refetch that
        * empties the list takes the grid with it, rather than leaving a family
        * ruling out classes they can no longer see. */}
      {wantsAnotherTime && anotherTimeTermId && hasClasses ? (
        <AnotherTimeRequest
          courseId={courseId}
          termId={anotherTimeTermId}
          childProfileId={selectedChild}
          blocked={!!block}
          onSubmitted={() => {
            setSubmittedKind('anotherTime');
            setSubmitted(true);
          }}
          onPendingChange={setAnotherTimePending}
        />
      ) : (
        <Button
          onClick={handleSubmit}
          // ⚠️ `selectedSlots.size === 0` is GONE from here too — see handleSubmit.
          disabled={!selectedChild || submitMutation.isPending || !!block}
          className="min-h-[44px] min-w-[44px] w-full bg-eifo-orange hover:bg-eifo-orange/85 text-white font-bold"
        >
          {submitMutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('submitting')}
            </>
          ) : hasClasses || sectionId ? (
            t('requestEnrollment')
          ) : (
            t('registerInterest')
          )}
        </Button>
      )}
    </div>
  );
}
